// client/src/config/roleMenus.js
// UPDATED WITH NEW PAGE-BASED PERMISSION SYSTEM BUT MAINTAINING ORIGINAL STRUCTURE

import React from 'react';
import HomeIcon                  from '@mui/icons-material/Home';
import SchoolIcon                from '@mui/icons-material/School';
import ClassIcon                 from '@mui/icons-material/Class';
import DescriptionIcon           from '@mui/icons-material/Description';
import PaymentIcon               from '@mui/icons-material/Payment';

import AssignmentIndIcon         from '@mui/icons-material/AssignmentInd';
import DashboardCustomizeIcon    from '@mui/icons-material/DashboardCustomize';
import FamilyRestroomIcon        from '@mui/icons-material/FamilyRestroom';

import SettingsIcon              from '@mui/icons-material/Settings';

import AccountBalanceIcon        from '@mui/icons-material/AccountBalance';
import ReceiptIcon               from '@mui/icons-material/Receipt';
import NotificationsActiveIcon   from '@mui/icons-material/NotificationsActive';
import BusinessCenterIcon        from '@mui/icons-material/BusinessCenter';
import BadgeIcon                 from '@mui/icons-material/Badge';
import MonetizationOnIcon        from "@mui/icons-material/MonetizationOn";
import AccountBalanceWalletIcon  from "@mui/icons-material/AccountBalanceWallet";
import AddIcon                   from '@mui/icons-material/Add';
import ListAltIcon               from '@mui/icons-material/ListAlt';
import QueueIcon                 from '@mui/icons-material/Queue';
import AssignmentTurnedInIcon    from '@mui/icons-material/AssignmentTurnedIn';
import SupervisorAccountIcon     from '@mui/icons-material/SupervisorAccount';
import CampaignIcon              from '@mui/icons-material/Campaign';
import GroupIcon                 from '@mui/icons-material/Group';
import VpnKeyIcon                from '@mui/icons-material/VpnKey';
import GavelIcon                 from '@mui/icons-material/Gavel';
import ApiIcon                   from '@mui/icons-material/Api';
import StorefrontIcon            from '@mui/icons-material/Storefront';
import SecurityIcon              from '@mui/icons-material/Security';
import BackupIcon                from '@mui/icons-material/Backup';
import BugReportIcon             from '@mui/icons-material/BugReport';
import HelpIcon                  from '@mui/icons-material/Help';
import BookIcon                  from '@mui/icons-material/Book';
import ReportProblemIcon         from '@mui/icons-material/ReportProblem';
import DesignServicesIcon        from '@mui/icons-material/DesignServices';
import ArticleIcon               from '@mui/icons-material/Article';
import PrintIcon                 from '@mui/icons-material/Print';
import AssessmentIcon            from '@mui/icons-material/Assessment';
import CalendarTodayIcon         from '@mui/icons-material/CalendarToday';
import LocalLibraryIcon          from '@mui/icons-material/LocalLibrary';
import TrendingUpIcon            from '@mui/icons-material/TrendingUp';
import DownloadIcon              from '@mui/icons-material/Download';
import ScheduleIcon              from '@mui/icons-material/Schedule';
import AutoFixHighIcon           from '@mui/icons-material/AutoFixHigh';
import MenuBookIcon              from '@mui/icons-material/MenuBook';
import EventIcon                 from '@mui/icons-material/Event';
import HomeWorkIcon              from '@mui/icons-material/HomeWork';
import DirectionsBusIcon         from '@mui/icons-material/DirectionsBus';
import HotelIcon                 from '@mui/icons-material/Hotel';
import InventoryIcon             from '@mui/icons-material/Inventory';
import SupportIcon               from '@mui/icons-material/Support';
import BuildIcon                 from '@mui/icons-material/Build';
import SchoolOutlinedIcon        from '@mui/icons-material/SchoolOutlined';
import PeopleIcon                from '@mui/icons-material/People';
import ScienceIcon               from '@mui/icons-material/Science';
import AttachMoneyIcon           from '@mui/icons-material/AttachMoney';
import BusinessIcon              from '@mui/icons-material/Business';
import AssessmentOutlinedIcon    from '@mui/icons-material/AssessmentOutlined';
import NotificationsIcon         from '@mui/icons-material/Notifications';
import EmailIcon                 from '@mui/icons-material/Email';
import PhoneIcon                 from '@mui/icons-material/Phone';
import AssignmentIcon            from '@mui/icons-material/Assignment';
import GradeIcon                 from '@mui/icons-material/Grade';
import EventNoteIcon             from '@mui/icons-material/EventNote';
import DirectionsCarIcon         from '@mui/icons-material/DirectionsCar';
import LocalHospitalIcon         from '@mui/icons-material/LocalHospital';
import ComputerIcon              from '@mui/icons-material/Computer';
import AccountCircleIcon         from '@mui/icons-material/AccountCircle';
import LockIcon                  from '@mui/icons-material/Lock';
import PaletteIcon               from '@mui/icons-material/Palette';
import LanguageIcon              from '@mui/icons-material/Language';
import MessageIcon               from '@mui/icons-material/Message';
import SwapHorizIcon             from '@mui/icons-material/SwapHoriz';
import HealthAndSafetyIcon       from '@mui/icons-material/HealthAndSafety';
import DevicesIcon               from '@mui/icons-material/Devices';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';

// Import the new permission system
import { ROLES, PAGE_PERMISSIONS, hasPageAccess, getInheritedRoles } from './pagePermissions';

// Optional helpers for phase- and feature-aware menu rendering
// These are backward-compatible: if no phase/features provided, they always allow.
const isPhaseAllowed = (item, activePhase) => {
  if (!item || !activePhase) return true;
  const phase = item.phase; // 'nur_pri' | 'sec' | 'all' | undefined
  if (!phase || phase === 'all') return true;
  if (Array.isArray(phase)) return phase.includes(activePhase);
  return phase === activePhase;
};

const isFeatureEnabled = (item, features) => {
  if (!item || !item.featureFlag) return true;
  return !!(features && features[item.featureFlag]);
};

/**
 * THE SCHOOL'S OWN VOCABULARY, per phase.
 *
 * `Class.classMasterId` carries two genuinely different jobs. In nursery and
 * primary the person is a CLASS TEACHER: they teach and mark the whole class.
 * In secondary they are a CLASS MASTER: they run the class without teaching all
 * of it. The admin screens have switched these words for a long time
 * (ClassAssignmentsPage and friends); the teacher's OWN navigation did not, so
 * a nur_pri class teacher's sidebar called them a class master — a word their
 * school does not use for them.
 *
 * A LABEL ONLY. The permission, the privilege string and the route are all
 * untouched; this renames the heading and nothing else.
 */
/**
 * DERIVED PRIVILEGES ARE NOT ROLES, and a menu entry gated on one must ask
 * whether the user HOLDS it — not whether they can open the page behind it.
 *
 * `ClassMaster` and `SubjectHead` are derived per request in
 * server/utils/derivePrivileges.js from what a person actually holds: being
 * `Class.classMasterId` of a class, or heading a subject or department. A
 * SchoolAdmin is neither, and yet PAGE_PERMISSIONS grants them /classmaster/*
 * and /subjecthead/* for oversight — deliberately, so their own screens can
 * call those endpoints.
 *
 * The sidebar filtered on page access alone, so the grant put a "Class master"
 * group (a "Class teacher" one in nursery/primary) and a "Head of department"
 * group into the admin's drawer. Those are a teacher's responsibilities, not an
 * administrator's, and the phone has never shown them: menuStructure gates the
 * same two groups on `requiresPrivilege`. This is the web catching up.
 *
 * SUBTRACTS ONLY. An entry declaring no derived role is untouched, and an
 * entry declaring one alongside ordinary roles still opens for anyone holding
 * one of those roles outright.
 */
const DERIVED_PRIVILEGE_BY_ROLE = {
  [ROLES.CLASS_MASTER]: 'ClassMaster',
  [ROLES.SUBJECT_HEAD]: 'SubjectHead',
};

const derivedGateOk = (node, userRoleArray, held) => {
  const declared = Array.isArray(node?.roles) ? node.roles : [];
  if (!declared.length) return true;

  const derived = declared.filter((r) => DERIVED_PRIVILEGE_BY_ROLE[r]);
  if (!derived.length) return true;

  // Held by role outright: no privilege needed.
  const ordinary = declared.filter((r) => !DERIVED_PRIVILEGE_BY_ROLE[r]);
  if (ordinary.some((r) => userRoleArray.includes(r))) return true;

  return derived.some((r) => held.has(DERIVED_PRIVILEGE_BY_ROLE[r]));
};

/**
 * ENTRIES THAT BELONG TO ANOTHER PORTAL, hidden by name.
 *
 * The gate above covers responsibilities nobody is assigned. This covers the
 * other half: pages an administrator legitimately HOLDS, for a reason that has
 * nothing to do with their navigation.
 *
 * PAGE_PERMISSIONS grants SchoolAdmin, Principal and HeadMaster
 * /teacher/attendance-recording, /teacher/student-grading and /teacher/dashboard
 * — and it must, because the ADMIN's own pages call those endpoints: the
 * Evaluation Control Center enters marks through /teacher/student-grading, and
 * Students Discipline reads through /teacher/class-discipline. Remove the grant
 * and the admin's own screens break.
 *
 * So the grant is about API access, not about navigation, and the sidebar has to
 * say so separately — which is precisely what menuStructure.js has said on the
 * phone all along, in `hideForRoles`. On the web nothing said it, so an
 * administrator was given a second "Dashboard" pointing at the teacher's, and a
 * Take attendance / Enter marks group for classes they do not teach.
 *
 * NAMED ROLES ONLY, like the phone's matcher: a school's own custom role — a
 * "Senior Teacher", a "Vice Principal" — is on no list and therefore sees
 * whatever it has been granted, which is the whole point of permission-driven
 * navigation. This hides one portal's entries from the built-in roles that hold
 * them for a different reason.
 */
const hiddenForRole = (node, userRoleArray) => {
  const list = node?.hideForRoles;
  if (!Array.isArray(list) || !list.length) return false;
  return (userRoleArray || []).some((role) => (
    role && list.some((r) => String(r).toLowerCase() === String(role).toLowerCase())
  ));
};

/** Drop every group and item this user should not be navigating to. */
const applyDerivedGates = (menu, userRoleArray, privileges) => {
  const held = new Set(Array.isArray(privileges) ? privileges : []);
  const keep = (node) => derivedGateOk(node, userRoleArray, held) && !hiddenForRole(node, userRoleArray);
  return (Array.isArray(menu) ? menu : [])
    .map((item) => {
      if (!item) return null;
      if (!keep(item)) return null;
      if (!Array.isArray(item.subMenus)) return item;
      const subMenus = item.subMenus.filter(keep);
      // A group emptied by the gate goes with its children: a heading with
      // nothing under it reads as a page that failed to load.
      return subMenus.length ? { ...item, subMenus } : null;
    })
    .filter(Boolean);
};

const phaseLabelled = (menu, activePhase) => {
  if (String(activePhase || '').toLowerCase() !== 'nur_pri') return menu;
  return (Array.isArray(menu) ? menu : []).map((item) => (
    item && item.textKey === 'Class master' ? { ...item, textKey: 'Class teacher' } : item
  ));
};

// eslint-disable-next-line import/no-anonymous-default-export
 const menusByRole = {
  // **SYSTEM ADMIN** - System-wide management
  
  SystemAdmin: [
    { textKey: 'Overview', icon: <AssignmentTurnedInIcon />, path: '/super-admin/system-overview', roles: [ROLES.SYSTEM_ADMIN] },
    {
      textKey: 'Schools',
      icon: <SchoolIcon />,
      roles: [ROLES.SYSTEM_ADMIN],
      subMenus: [
        { textKey: 'School Directory', icon: <SchoolIcon />, path: '/super-admin/schools', roles: [ROLES.SYSTEM_ADMIN] },
        { textKey: 'Registration Queue', icon: <QueueIcon />, path: '/super-admin/registration-queue', roles: [ROLES.SYSTEM_ADMIN] },
        { textKey: 'Onboarding Status', icon: <AssignmentTurnedInIcon />, path: '/super-admin/onboarding-status', roles: [ROLES.SYSTEM_ADMIN] }
      ]
    },
    { textKey: 'Billing & Subscriptions', icon: <PaymentIcon />, path: '/super-admin/billing', roles: [ROLES.SYSTEM_ADMIN] },
    { textKey: 'Users & Access', icon: <GroupIcon />, path: '/super-admin/users-access', roles: [ROLES.SYSTEM_ADMIN] },
    {
      textKey: 'Platform Config',
      icon: <SettingsIcon />,
      roles: [ROLES.SYSTEM_ADMIN],
      subMenus: [
        { textKey: 'Settings', icon: <SettingsIcon />, path: '/super-admin/system-settings', roles: [ROLES.SYSTEM_ADMIN] },
        { textKey: 'Communications', icon: <CampaignIcon />, path: '/super-admin/communications', roles: [ROLES.SYSTEM_ADMIN] },
        { textKey: 'Integrations', icon: <IntegrationInstructionsIcon />, path: '/super-admin/integrations', roles: [ROLES.SYSTEM_ADMIN] }
      ]
    },
    { textKey: 'Security & Audit', icon: <SecurityIcon />, path: '/super-admin/security', roles: [ROLES.SYSTEM_ADMIN] },
    { textKey: 'Support Tickets', icon: <SupportIcon />, path: '/super-admin/support-tickets', roles: [ROLES.SYSTEM_ADMIN] }
  ],

  ITManager: [
    { 
      textKey: 'IT Services', 
      icon: <DevicesIcon />, 
      roles: ['ITManager', ROLES.SYSTEM_ADMIN],
      subMenus: [
        { textKey: 'IT Dashboard', icon: <DashboardCustomizeIcon />, path: '/super-admin/it/dashboard', roles: ['ITManager', ROLES.SYSTEM_ADMIN] },
        { textKey: 'System Monitoring', icon: <AssessmentIcon />, path: '/super-admin/it/monitoring', roles: ['ITManager', ROLES.SYSTEM_ADMIN] },
        { textKey: 'Backup Management', icon: <BackupIcon />, path: '/super-admin/it/backup', roles: ['ITManager', ROLES.SYSTEM_ADMIN] },
        { textKey: 'Security Audit', icon: <SecurityIcon />, path: '/super-admin/it/security', roles: ['ITManager', ROLES.SYSTEM_ADMIN] },
        { textKey: 'API Management', icon: <ApiIcon />, path: '/super-admin/it/api', roles: ['ITManager', ROLES.SYSTEM_ADMIN] },
      ]
    },
  ],

  SalesRep: [
    { textKey: 'Schools', icon: <SchoolIcon />, path: '/super-admin/schools', roles: ['SalesRep'] },
    { textKey: 'Onboarding Status', icon: <AssignmentTurnedInIcon />, path: '/super-admin/onboarding-status', roles: ['SalesRep'] },
  ],

  SupportAgent: [
    { textKey: 'Registration Queue', icon: <QueueIcon />, path: '/super-admin/registration-queue', roles: ['SupportAgent'] },
    { textKey: 'Onboarding Status', icon: <AssignmentTurnedInIcon />, path: '/super-admin/onboarding-status', roles: ['SupportAgent'] },
    { textKey: 'Support Tickets', icon: <SupportIcon />, path: '/super-admin/support-tickets', roles: ['SupportAgent'] },
  ],

  // **SCHOOL ADMIN** - School-level management (includes Principal/HeadTeacher)
  SchoolAdmin: [
    {
      textKey: 'Dashboard',
      icon: <HomeIcon />,
      roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
      subMenus: [
        { textKey: 'School Overview', icon: <SchoolIcon />, path: '/school-overview', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Quick Actions', icon: <AssignmentTurnedInIcon />, path: '/quick-actions', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
      ]
    },
    
    {
      textKey: 'Class Management',
      icon: <ClassIcon />,
      roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
      subMenus: [
        { textKey: 'Class Overview', icon: <DashboardCustomizeIcon />, path: '/class-management', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Add New Class', icon: <AddIcon />, path: '/class-management/add-class', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Manage Classes', icon: <SettingsIcon />, path: '/class-management/manage-classes', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Class Assignments', icon: <AssignmentIndIcon />, path: '/class-management/class-assignments', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Class Reports', icon: <AssessmentIcon />, path: '/class-management/class-reports', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
      ]
    },
    
    {
      textKey: 'Staff Management',
      icon: <SupervisorAccountIcon />,
      roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
      subMenus: [
        { textKey: 'Role Permissions', icon: <SecurityIcon />, path: '/role-permissions', roles: [ROLES.SCHOOL_ADMIN] },
        { textKey: 'User Management', icon: <PeopleIcon />, path: '/user-list', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'User Form', icon: <AddIcon />, path: '/user-form', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Staff Evaluation', icon: <AssessmentIcon />, path: '/staff-evaluation', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        
        
      ]
    },
    {
      textKey: 'Subject Management',
      icon: <ScienceIcon />,
      roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
      subMenus: [
        { textKey: 'Subject Overview', icon: <DashboardCustomizeIcon />, path: '/subject-management', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL] },
        { textKey: 'Add New Subject', icon: <AddIcon />, path: '/subject-management/add-subject', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL] },
        { textKey: 'Manage Subjects', icon: <SettingsIcon />, path: '/subject-management/manage-subjects', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL] },
        { textKey: 'Subject Assignments', icon: <AssignmentIndIcon />, path: '/subject-management/subject-assignments', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL], phase: 'sec' },
        { textKey: 'Subject Reports', icon: <AssessmentIcon />, path: '/subject-management/subject-reports', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL] },
      ]
    },
    {
      textKey: 'Student Management',
      icon: <FamilyRestroomIcon />,
      roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
      subMenus: [
        { textKey: 'Admissions', icon: <QueueIcon />, path: '/student-admissions', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Student Directory', icon: <PeopleIcon />, path: '/student-directory', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Attendance Summary', icon: <AssessmentIcon />, path: '/attendance-summary', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Students Discipline', icon: <GavelIcon />, path: '/students-discipline', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Class Promotion Summary', icon: <ClassIcon />, path: '/class-promotion-summary', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'ID Card Workshop', icon: <BadgeIcon />, path: '/id-card-workshop', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL], phase: ['nur_pri', 'sec'] },
      ]
    },
    {
      textKey: 'Academics & Exams',
      icon: <SchoolIcon />,
      roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
      subMenus: [
        { textKey: 'Evaluation Control Center', icon: <GradeIcon />, path: '/evaluation-control-center', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Academic Calendar', icon: <CalendarTodayIcon />, path: '/calendar', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Timetable', icon: <ScheduleIcon />, path: '/timetable', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        // Secondary only — nursery/primary has one class teacher per class, so
        // there is nothing to generate. HeadMaster is left off for that reason,
        // not as an oversight.
        { textKey: 'Timetable Generator', icon: <AutoFixHighIcon />, path: '/timetable-generator', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL], phase: 'sec' },
        { textKey: 'Grading Scales', icon: <TrendingUpIcon />, path: '/grading-scales', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Report Card Studio', icon: <DescriptionIcon />, path: '/report-card-studio', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Academic Transcripts', icon: <DescriptionIcon />, path: '/student-transcripts', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER], phase: 'sec' },
      ]
      },
    // Teacher portal, matching the mobile drawer.
    //
    // These were one "Teaching & Learning" group with labels of their own
    // ("Student Grading", "Attendance Recording", "Class Performance"), while
    // mobile split them into Academics / Class master / Head of department and
    // named them for the action. Two vocabularies for one product meant a
    // teacher moving between phone and desktop had to relearn the menu.
    //
    // Both clients translate with t(`menus.${label}`) and the English label IS
    // the key, so aligning the labels also makes them share translations — a
    // wording change now lands in both at once.
    //
    // The split is not cosmetic either: ClassMaster and SubjectHead are DERIVED
    // privileges, granted only to teachers who actually run a class or head a
    // department. Giving each its own heading makes that responsibility legible
    // instead of mixing it in with ordinary teaching.
    {
      // THE TEACHER'S HOME, not the administrator's. An admin already has a
      // Dashboard group above (School Overview, Quick Actions), so this arrived
      // as a second entry of the same name pointing somewhere else entirely.
      textKey: 'Dashboard',
      icon: <DashboardCustomizeIcon />,
      path: '/teacher/dashboard',
      roles: [ROLES.TEACHER],
      hideForRoles: ['SchoolAdmin', 'Principal', 'HeadMaster'],
    },
    {
      // TEACHING, kept separate from the "Academics & Exams" group above: that
      // one is the administrator's, this one is the teacher's. An admin does not
      // take a register; the teacher who teaches that class does. Same split,
      // and the same hideForRoles, as the phone's `academics` group.
      textKey: 'Academics',
      icon: <SchoolOutlinedIcon />,
      roles: [ROLES.TEACHER],
      hideForRoles: ['SchoolAdmin', 'Principal', 'HeadMaster'],
      subMenus: [
        { textKey: 'Take attendance', icon: <AssignmentTurnedInIcon />, path: '/teacher/attendance-recording', roles: [ROLES.TEACHER] },
        // THE CANONICAL PATH, matching the route's own permission check, the
        // server's PAGE_PERMISSIONS row and the phone's registered path.
        //
        // This pointed at `/teacher/marks`, which the SERVER has no row for — so
        // the entry survived only through the client-side half of mayAccess's
        // union. It worked, but it meant the menu, the route guard and the
        // server were naming the same page three different ways, and the two
        // links that used the server's name went to the landing page instead.
        { textKey: 'Enter marks', icon: <GradeIcon />, path: '/teacher/student-grading', roles: [ROLES.TEACHER] },
      ]
    },
    {
      textKey: 'Class master',
      icon: <TrendingUpIcon />,
      roles: [ROLES.CLASS_MASTER],
      subMenus: [
        { textKey: 'Class results', icon: <TrendingUpIcon />, path: '/classmaster/class-academic-performance', roles: [ROLES.CLASS_MASTER] },
        { textKey: 'Class discipline', icon: <GavelIcon />, path: '/classmaster/class-discipline', roles: [ROLES.CLASS_MASTER] },
      ]
    },
    {
      textKey: 'Head of department',
      icon: <AssessmentIcon />,
      roles: [ROLES.SUBJECT_HEAD],
      subMenus: [
        // Two entries for what the page presents as two tabs. The route decides
        // which tab opens, so each entry lands on the half it names — before
        // this, both opened on performance and the reports half was reachable
        // only by spotting the tab.
        //
        // BOTH are secondary. Head of department is a secondary role: nursery
        // and primary have class teachers and subject teachers, not
        // departments. Dropping the gate from the report was my mistake.
        { textKey: 'Subject performance', icon: <TrendingUpIcon />, path: '/subjecthead/subject-performance-matrix', roles: [ROLES.SUBJECT_HEAD], phase: 'sec' },
        { textKey: 'Departmental report', icon: <AssessmentIcon />, path: '/subjecthead/subject-report', roles: [ROLES.SUBJECT_HEAD], phase: 'sec' },
      ]
    },
    {
      textKey: 'Finance',
      icon: <AttachMoneyIcon />,
      roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER],
      subMenus: [
        { textKey: 'Fee Types', icon: <AccountBalanceIcon />, path: '/fee-types', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER] },
        { textKey: 'Fees', icon: <ReceiptIcon />, path: '/fee-structure-manager', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER] },
        // ONE entry: the two fee pages were merged. /manage-fee-payments still
        // resolves to the same page for anyone granted only that permission.
        { textKey: 'Fee Payments', icon: <PaymentIcon />, path: '/payment-processing', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER] },
        { textKey: 'Expenses', icon: <AccountBalanceWalletIcon />, path: '/expenses', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER] },
        { textKey: 'Budgets', icon: <MonetizationOnIcon />, path: '/budgets', roles: [ROLES.SCHOOL_ADMIN, ROLES.FINANCE_OFFICER] },
        { textKey: 'Financial Reports', icon: <AssessmentIcon />, path: '/financial-reports', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.FINANCE_OFFICER] },
        { textKey: 'Salary Management', icon: <PaymentIcon />, path: '/salary-management', roles: [ROLES.SCHOOL_ADMIN] },
      ]
    },
    {
      textKey: 'Facilities',
      icon: <BusinessCenterIcon />,
      roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
      subMenus: [
        { textKey: 'Library Dashboard', icon: <DashboardCustomizeIcon />, path: '/facilities/library-dashboard', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.LIBRARIAN] },
        { textKey: 'Transport Routes', icon: <DirectionsBusIcon />, path: '/facilities/transport-routes', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.TRANSPORT_OFFICER] },
        { textKey: 'Inventory Control', icon: <InventoryIcon />, path: '/facilities/inventory-control', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.INVENTORY_MANAGER] },
      ]
    },

    {
      textKey: 'Communication',
      icon: <NotificationsIcon />,
      roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
      subMenus: [
        { textKey: 'Announcements', icon: <CampaignIcon />, path: '/communication/announcements', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Messages', icon: <EmailIcon />, path: '/communication/messages', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER, ROLES.TEACHER, ROLES.STUDENT, ROLES.PARENT] },
      ]
    },
    {
      textKey: 'Support & Maintenance',
      icon: <SupportIcon />,
      roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
      subMenus: [
        { textKey: 'Submit Ticket', icon: <BugReportIcon />, path: '/support/submit-ticket', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Help Center', icon: <HelpIcon />, path: '/support/help-center', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
      ]
    },
    {
      textKey: 'School Setup & Configuration',
      icon: <SettingsIcon />,
      roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER],
      subMenus: [
        { textKey: 'School Settings', icon: <SettingsIcon />, path: '/school-setup-and-configuration/school-settings', roles: [ROLES.SCHOOL_ADMIN, ROLES.HEAD_MASTER, ROLES.PRINCIPAL] },
        { textKey: 'Communication Settings', icon: <PhoneIcon />, path: '/school-setup-and-configuration/communication-settings', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Academic Sessions', icon: <EventIcon />, path: '/school-setup-and-configuration/academic-sessions', roles: [ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
        { textKey: 'Backup & Security', icon: <SecurityIcon />, path: '/school-setup-and-configuration/backup-security', roles: [ ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.HEAD_MASTER] },
      ]
    }
  ],



  // **STUDENT** - Student access (3 essential pages)
  Student: [
    { textKey: 'Messages', icon: <EmailIcon />, path: '/student/messages', roles: [ROLES.STUDENT] },
    { textKey: 'My Profile', icon: <AccountCircleIcon />, path: '/my-profile', roles: [ROLES.STUDENT] }
  ],

  // **PARENT** - Parent access (5 essential pages)
    Parent: [
    { textKey: 'Dashboard', icon: <DashboardCustomizeIcon />, path: '/parent/dashboard', roles: [ROLES.PARENT] },
    { textKey: 'Children & Academics', icon: <FamilyRestroomIcon />, path: '/parent/children-academics', roles: [ROLES.PARENT] },
    { textKey: 'Attendance Records', icon: <AssignmentTurnedInIcon />, path: '/parent/attendance', roles: [ROLES.PARENT] },
    { textKey: 'Fees & Payments', icon: <PaymentIcon />, path: '/parent/fees-payments', roles: [ROLES.PARENT] },
    { textKey: 'Messages', icon: <EmailIcon />, path: '/communication/messages', roles: [ROLES.PARENT] }
  ],

  // **DYNAMIC MENU GENERATION FUNCTIONS** - New permission system
  // Function to check if user has access to a menu item
  hasMenuAccess: (userRoles, requiredRoles) => {
    if (!userRoles || !requiredRoles) return false;
    
    // Check if user has any of the required roles
    const userRoleArray = Array.isArray(userRoles) ? userRoles : [userRoles];
    const requiredRoleArray = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
    
    // Check if user has ALL required roles (for items that need multiple roles like Teacher + ClassMaster)
    if (requiredRoleArray.length > 1) {
      return requiredRoleArray.every(requiredRole => 
        userRoleArray.includes(requiredRole)
      );
    }
    
    // Check if user has any of the required roles (for single role requirements)
    return userRoleArray.some(userRole => {
      // Get inherited roles for the user's role
      const inheritedRoles = getInheritedRoles(userRole);
      return inheritedRoles.some(role => requiredRoleArray.includes(role));
    });
  },

  // Function to filter menu items based on user roles, phase, and features
  filterMenuByRoles: (menuStructure, userRoles, options = {}) => {
    if (!userRoles) return [];
    
    const userRoleArray = Array.isArray(userRoles) ? userRoles : [userRoles];
    const { phase: activePhase, features, privileges = [] } = options;
    
    return Object.values(menuStructure).filter(menuItem => {
      // Check if user has access to this menu item
      if (!menuStructure.hasMenuAccess(userRoleArray, menuItem.roles)) {
        return false;
      }
      // Optional phase/feature checks for the menu group itself
      if (!isPhaseAllowed(menuItem, activePhase)) return false;
      if (!isFeatureEnabled(menuItem, features)) return false;
      
      // Filter submenus if they exist
      if (menuItem.subMenus) {
        // Prefer path-based permission when available to make menus permission-driven
        menuItem.subMenus = menuItem.subMenus.filter(subItem => {
          if (!isPhaseAllowed(subItem, activePhase)) return false;
          if (!isFeatureEnabled(subItem, features)) return false;
          if (subItem.path) {
            return hasPageAccess(userRoleArray, subItem.path, privileges);
          }
          return menuStructure.hasMenuAccess(userRoleArray, subItem.roles);
        });
        
        // Only show menu item if it has accessible submenus or is a direct link
        return menuItem.subMenus.length > 0 || menuItem.path;
      }
      
      return true;
    });
  },

  // Function to generate complete menu for a user, with optional { phase, features }
  // `allowPath` lets the caller supply the SERVER's answer to "may I open this
  // page". The phone asks the server and shows what comes back; the web has
  // always re-derived it here from a client-side copy of PAGE_PERMISSIONS, and
  // every teacher-portal bug for weeks has been somewhere in that re-derivation.
  // When it is passed, a group survives if the server grants any of its pages —
  // so a class master or head of department cannot lose their menu to a local
  // rule that disagrees with the server enforcing the request.
  generateMenu: (userRoles, options = {}) => {
    if (!userRoles) return [];
    
    const userRoleArray = Array.isArray(userRoles) ? userRoles : [userRoles];
    const { phase: activePhase, features, privileges = [], allowPath } = options;
    const mayOpen = typeof allowPath === 'function'
      ? allowPath
      : (path) => hasPageAccess(userRoleArray, path, privileges);
    // A page the SERVER grants is not hidden by the client's phase guess. The
    // `phase` flags here are a UI hint, resolved from privileges that may not
    // have arrived yet; the Head of department items carry phase: 'sec' and were
    // being dropped before the grant was ever consulted, which removed the group
    // from exactly the people it belongs to.
    // A DECLARED PHASE MISMATCH WINS once the phase is actually known.
    //
    // This was `isPhaseAllowed(...) || allowPath(node.path)`, and allowPath
    // consults the server's granted-page list — so `phase: 'sec'` on the Head of
    // department entries was defeated whenever that list contained them, which
    // it does whenever /my-access was fetched before the phase header existed.
    //
    // The grant still wins while the phase is UNKNOWN, which is the case the
    // union was written for: an unresolved phase must not hide pages the server
    // is granting. It just stops winning once we know the answer.
    const phaseOk = (node) => (
      activePhase
        ? isPhaseAllowed(node, activePhase)
        : (isPhaseAllowed(node, activePhase)
          || (typeof allowPath === 'function' && node?.path && allowPath(node.path)))
    );
    
    // Get the base menu for the user's primary role
    const primaryRole = userRoleArray[0];
    let baseMenu = menusByRole[primaryRole] || [];

    // Principal/HeadMaster should use the SchoolAdmin menu structure (filtered by page access)
    if (['Principal', 'HeadMaster'].includes(primaryRole)) {
      baseMenu = menusByRole['SchoolAdmin'] || [];
    }
    
    // Special handling for System Admin - they have access to ALL roles and schools
    if (userRoleArray[0] === 'SystemAdmin') {
      // Super Admin gets access to all school-level roles and their pages
      // No additional pages needed for SuperAdmin - all pages are already in the main menu
      // baseMenu = baseMenu;
    }
    
    // Special handling for School Admin, Principal/HeadTeacher - they have access to all school-level roles
    if (['SchoolAdmin', 'Principal', 'HeadMaster', 'PrincipalHeadTeacher'].includes(primaryRole)) {
      // Add menus from other school-level roles
      const schoolLevelRoles = ['Teacher', 'FinanceOfficer', 'Librarian', 'TransportOfficer', 'InventoryManager', 'NurseHealthOfficer', 'ITSupport', 'AdmissionsOfficer'];
      
      schoolLevelRoles.forEach(role => {
        if (role !== primaryRole) {
          const roleMenu = menusByRole[role] || [];
          baseMenu = [...baseMenu, ...roleMenu];
        }
      });
      
      // No additional pages needed for SchoolAdmin/PrincipalHeadTeacher - all pages are already in the main menu
      // baseMenu = baseMenu;
    }
    
    // For staff roles (Teacher, ClassMaster, SubjectHead, etc.), they should see the School Admin menu
    // but filtered to only show pages they have permission for (path-driven)
    if (['Teacher', 'ClassMaster', 'SubjectHead', 'FinanceOfficer', 'Librarian', 'TransportOfficer', 'InventoryManager', 'NurseHealthOfficer', 'ITSupport', 'AdmissionsOfficer'].includes(userRoleArray[0])) {
      // Get the School Admin menu and filter it based on user's roles
      const schoolAdminMenu = menusByRole['SchoolAdmin'] || [];
      // Deep-filter submenus using PAGE_PERMISSIONS via hasPageAccess
      baseMenu = schoolAdminMenu
        .map(menuItem => {
          const cloned = { ...menuItem };
          if (Array.isArray(menuItem.subMenus)) {
            cloned.subMenus = menuItem.subMenus.filter(subItem => {
              if (!phaseOk(subItem)) return false;
              if (!isFeatureEnabled(subItem, features)) return false;
              if (subItem.path) {
                return mayOpen(subItem.path);
              }
              return menusByRole.hasMenuAccess(userRoleArray, subItem.roles);
            });
          }
          return cloned;
        })
        // Keep menu groups that have at least one allowed submenu or a direct path allowed
        .filter(menuItem => {
          if (!phaseOk(menuItem)) return false;
          if (!isFeatureEnabled(menuItem, features)) return false;
          if (menuItem.subMenus && menuItem.subMenus.length > 0) return true;
          if (menuItem.path) return mayOpen(menuItem.path);
          return false;
        });

      // The teacher sidebar keeps its groups, matching the mobile drawer.
      //
      // It used to be flattened into a single list on the grounds that a
      // teacher's few pages did not warrant headings. That was true when they
      // were a handful of items borrowed from the School Admin menu; it is not
      // true now. Academics, Class master and Head of department are different
      // KINDS of responsibility, and the last two are derived privileges the
      // server grants only to teachers who actually run a class or head a
      // department. Flattening them threw that distinction away and left the
      // phone and the desktop presenting the same pages in different shapes.
      //
      // Groups with no permitted children are already dropped by the filter
      // above, so a plain classroom teacher still sees only Dashboard and
      // Academics — no empty headings.
      //
      // De-duplication still matters: the same path can appear under more than
      // one School Admin group, and rendering it twice looks like a bug.
      if (userRoleArray[0] === 'Teacher') {
        const seen = new Set();
        baseMenu = baseMenu
          .map((item) => {
            if (!item) return null;
            if (Array.isArray(item.subMenus)) {
              const subMenus = item.subMenus.filter((sub) => {
                const p = sub?.path ? String(sub.path) : '';
                if (!p || seen.has(p)) return false;
                seen.add(p);
                return true;
              });
              return subMenus.length ? { ...item, subMenus } : null;
            }
            const p = item.path ? String(item.path) : '';
            if (!p || seen.has(p)) return null;
            seen.add(p);
            return item;
          })
          .filter(Boolean);
      }
    }
    
    // If user has multiple roles, add additional menu items from other roles
    if (userRoleArray.length > 1) {
      const additionalMenus = userRoleArray.slice(1).map(role => (menusByRole[role] || []));
      // Apply optional filters to additional menus too
      const filteredAdditional = additionalMenus.flat().map(menuItem => {
        const cloned = { ...menuItem };
        if (!isPhaseAllowed(cloned, activePhase) || !isFeatureEnabled(cloned, features)) return null;
        if (Array.isArray(cloned.subMenus)) {
          cloned.subMenus = cloned.subMenus.filter(subItem => {
            if (!isPhaseAllowed(subItem, activePhase)) return false;
            if (!isFeatureEnabled(subItem, features)) return false;
            if (subItem.path) return hasPageAccess(userRoleArray, subItem.path, privileges);
            return menusByRole.hasMenuAccess(userRoleArray, subItem.roles);
          });
        }
        return cloned;
      }).filter(Boolean);
      return phaseLabelled(applyDerivedGates([...baseMenu, ...filteredAdditional], userRoleArray, privileges), activePhase);
    }

    return phaseLabelled(applyDerivedGates(baseMenu, userRoleArray, privileges), activePhase);
  }
};

export default menusByRole;
